<template>
  <article class="payment-dutch">
    <!-- 더치페이 헤더 -->
    <PaymentDutchHeader type="menu" />

    <div class="dutch-menu">
      <ul class="dutch-menu__ul list-style-none">
        <li v-for="(menu, index) in sortedDutchMenus" :key="index">
          <PaymentDutchMenuItem :dutch-menu="menu" />
        </li>
      </ul>
    </div>

    <footer class="dutch-menu__footer">
      <div class="dutch-menu__info">
        <div class="info__qty">
          <span class="info__text">{{ t('label.qty') }}</span>
          <span class="info__number">{{ selectedDutchMenusCount }}</span>
        </div>
        <div class="info__amount">
          <span class="info__text">{{ t('label.total_amount') }}</span>
          <span class="info__number info__number--red">{{ formatCurrency(selectedDutchMenusAmount) }}</span>
        </div>
      </div>
      <div class="dutch-menu__button-group">
        <BaseButtonGroup>
          <BaseButton class="outline" :disabled="isPrevButtonDisabled" @button-click="handlePrevClick">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
                <path
                  id="arrow-left-solid"
                  d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                  transform="translate(-0.025 -63.925)"
                />
              </svg>
            </template>
            {{ t('button.prev') }}
          </BaseButton>
          <BaseButton class="BaseButton--black" @button-click="handleCardClick">
            {{ t('button.pay_card') }}
          </BaseButton>
          <BaseButton v-if="configStore.getIsCashPaymentUsed()" @button-click="handleCashClick">
            {{ t('button.pay_cash') }}
          </BaseButton>
          <BaseButton
            v-if="configStore.config.IsEasyPaymentUsed"
            class="BaseButton--burgundy"
            @button-click="handleEasyClick"
          >
            {{ t('button.pay_easy') }}
          </BaseButton>
        </BaseButtonGroup>
      </div>
    </footer>
  </article>
</template>

<script setup lang="ts">
  import OrderComplete from '@/components/OrderComplete.vue';
  import PaymentDutchHeader from '@/components/PaymentDutchHeader.vue';
  import PaymentDutchMenuItem from '@/components/PaymentDutchMenuItem.vue';
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import { useOrder } from '@/composable/useOrder';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import UserError from '@/error';
  import CartMenu from '@/interfaces/CartMenu';
  import DutchMenu from '@/interfaces/DutchMenu';
  import IPaymentSplit from '@/interfaces/PaymentSplit';
  import { logger } from '@/logger';
  import { useCartStore } from '@/stores/cart';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { useModalStore } from '@/stores/modal';
  import { useOrderStore } from '@/stores/order';
  import { usePaymentStore } from '@/stores/payment';
  import { formatCurrency, getMenuTaxAmount } from '@/utils';
  import { v4 as uuid } from 'uuid';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import PaymentCardModal from './PaymentCardModal.vue';
  import PaymentEasyModal from './PaymentEasyModal.vue';

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const cartStore = useCartStore();
  const orderStore = useOrderStore();
  const paymentStore = usePaymentStore();
  const menuStore = useMenuStore();

  const { openReceiptOrComplete } = useOrder();

  // 더치페이 메뉴나눔 항목
  const dutchMenus = ref<DutchMenu[]>([]);

  // 결제 미완료한 메뉴를 앞으로 정렬한 더치페이 메뉴 목록
  const sortedDutchMenus = computed(() => {
    return [...dutchMenus.value].sort((a, b) => Number(a.IsPaid) - Number(b.IsPaid));
  });

  // 선택된 상품 수량
  const selectedDutchMenusCount = computed(() => dutchMenus.value.filter((menu) => menu.IsChecked).length);

  // 선택된 상품 합산 금액
  const selectedDutchMenusAmount = computed(() =>
    dutchMenus.value?.reduce((acc, dutchMenu) => acc + (dutchMenu.IsChecked ? dutchMenu.TotalPrice : 0), 0),
  );

  // 이전 버튼 비활성화 여부
  const isPrevButtonDisabled = computed(() => dutchMenus.value.some((menu) => menu.IsPaid));

  watch(
    () => paymentStore.remainingAmount,
    async (newVal) => {
      // 포인트 결제 후 남은 결제 금액이 0원이 된 경우 결제 완료 처리
      if (newVal === 0) {
        // 주문 전송
        await order();
      }
    },
  );

  onMounted(() => {
    // (메뉴나눔 더치페이) 더치페이 메뉴 초기화
    setDutchMenus();
  });

  /** 이전 버튼 클릭 */
  const handlePrevClick = () => modalStore.goBackFullScreenModal();

  /** 현금 결제 버튼 클릭 */
  const handleCashClick = async () => {
    logger.info('[PaymentDutchMenu] 현금 결제 선택');
    if (!dutchMenus.value.some((menu) => menu.IsChecked)) {
      throw new UserError(t('error.select_menu'));
    }

    const isOK = await commonStore.showDialog({
      type: 'confirm',
      title: t('label.take_order'),
      message: t('general.cash_pay_message'),
    });
    if (!isOK) return;

    // 결제 데이터를 현금 결제로 주문에 추가
    paymentStore.addPaymentSplit(createPaymentSplit(PaymentMethod.Cash));

    // 현재 선택된 항목을 현금결제 처리
    setDutchMenuOrderFlag(PaymentMethod.Cash);
  };

  /** 카드 결제 버튼 클릭 */
  const handleCardClick = async () => {
    logger.info('[PaymentDutchMenu] 카드 결제 선택');
    if (!dutchMenus.value.some((menu) => menu.IsChecked)) {
      throw new UserError(t('error.select_menu'));
    }

    const isSuccess = await modalStore.openSyncModalAsync<typeof PaymentCardModal, boolean>(PaymentCardModal, {
      amount: selectedDutchMenusAmount.value,
    });
    if (!isSuccess) return;

    // 더치페이 메뉴 항목을 카드 결제로 완료 처리
    setDutchMenuOrderFlag(PaymentMethod.Card);
  };

  /** 간편 결제 버튼 클릭 */
  const handleEasyClick = async () => {
    logger.info('[PaymentDutchMenu] 간편 결제 선택');
    if (!dutchMenus.value.some((menu) => menu.IsChecked)) {
      throw new UserError(t('error.select_menu'));
    }

    // 간편 결제 진행 후 성공 여부 반환
    const isSuccess = await modalStore.openSyncModalAsync<typeof PaymentEasyModal, boolean>(PaymentEasyModal, {
      amount: selectedDutchMenusAmount.value,
    });

    // 간편 결제 실패한 경우 종료
    if (!isSuccess) return;

    // 더치페이 메뉴 항목을 카드 결제로 완료 처리
    setDutchMenuOrderFlag(PaymentMethod.Easy);
  };

  /**
   * (메뉴나눔 더치페이) 결제완료 데이터 설정
   * @param payType 결제완료 수단
   */
  const setDutchMenuOrderFlag = (paymentMethod: PaymentMethod) => {
    dutchMenus.value
      .filter((menu) => menu.IsChecked)
      .forEach((menu) => {
        menu.IsChecked = false;
        menu.IsPaid = true;
        menu.PayMethod = paymentMethod;
      });
    logger.info(`[PaymentDutchMenu] 더치페이 메뉴 결제 완료 처리. PaymentMethod=${paymentMethod}`, dutchMenus.value);
  };

  /** (메뉴나눔 더치페이) 더치페이 메뉴 초기화 */
  const setDutchMenus = () => {
    dutchMenus.value = cartStore.cart.CartMenus.flatMap((cartMenu) => {
      const totalPrice = cartStore.getCartMenuPrice(cartMenu, 1, true);

      const data: CartMenu = {
        ...cartMenu,
        Qty: 1,
      };

      // 3. 기존 메뉴의 수량(Qty)만큼 새로운 DutchMenu 객체를 배열로 생성합니다.
      return Array.from({ length: cartMenu.Qty }, () => ({
        Id: uuid(), // 각 메뉴에 고유한 ID를 부여합니다.
        IsChecked: false,
        IsPaid: totalPrice === 0,
        TotalPrice: totalPrice,
        Data: data,
      }));
    });

    logger.info('[PaymentDutchMenu] 더치페이 메뉴 초기화', dutchMenus.value);
  };

  /** 현재 선택된 메뉴 기준으로 결제 데이터 생성 */
  const createPaymentSplit = (method: PaymentMethod): IPaymentSplit => {
    // 결제 금액 변수 설정
    let amount = 0;
    let taxFree = 0;
    let tax = 0;

    // 선택된 메뉴 항목에 대해 결제 금액 계산
    dutchMenus.value
      .filter((menu) => menu.IsChecked)
      .forEach((dutchMenu) => {
        const menu = menuStore.getMenu(dutchMenu.Data.GroupCode, dutchMenu.Data.ItemCode);
        const taxAmount = getMenuTaxAmount(dutchMenu.TotalPrice, menu.IsTax, menu.IsTax2);

        amount += dutchMenu.TotalPrice;
        taxFree += taxAmount === 0 ? dutchMenu.TotalPrice : 0;
        tax += taxAmount;
      });

    // 결제 데이터 설정 후 반환
    return {
      method,
      amount,
      taxFree,
      tax,
      deposit: 0,
      preTip: 0,
    };
  };

  /** 더치페이 메뉴 항목 모두 현금 결제인 경우 주문 진행 */
  const order = async () => {
    logger.info('[PaymentDutchMenu] 주문 전송 시작');
    const loadingId = commonStore.showLoading();
    try {
      // POS에서 주문 조회 후 세팅
      await orderStore.fetchOrder(true);

      // 선결제인 경우 장바구니 데이터를 주문 데이터에 추가
      if (!orderStore.isPreOrder) {
        orderStore.addOrderItem(cartStore.cart.CartMenus);
      }

      // 모든 결제가 현금인지 여부 (주문 전송 후 결제 데이터 초기화 되기 때문에 주문 전송 이전에 위치해야함)
      const isCashOnly = paymentStore.summary.splits.every((split) => split.method === PaymentMethod.Cash);

      // 주문 전송
      await orderStore.sendOrder(true);

      // 모든 결제가 현금 결제인 경우 영수증 요청 생략하고 결제완료 모달 열기. 다른 결제가 있으면 영수증 모달 열기.
      if (isCashOnly) {
        modalStore.openFullScreenModal(OrderComplete, { hasPayment: false });
      } else {
        // 영수증 또는 주문완료 모달 열기
        openReceiptOrComplete(false);
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .payment-dutch {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto 1fr auto;
    height: 100vh;
    padding: 2.2388vw 2.8358vw 0;
    @media (max-width: vars.$sm) {
      grid-template-columns: 1fr;
    }
    .dutch-menu {
      grid-column: 1 / 4;
      padding-top: 2.9851vw;
      overflow-x: hidden;
      overflow-y: auto;
      height: 100%;
      @media (max-width: vars.$sm) {
        grid-column: 1 / 3;
      }
      .dutch-menu__ul {
        display: flex;
        flex-wrap: wrap;
        margin: 0 -0.7463vw;
        padding-bottom: 0.7463vw;
        li {
          width: 33.333333%;
          padding: 0 0.7463vw;
          margin-bottom: 1.4925vw;
          @media (max-width: vars.$md) {
            width: 50%;
          }
        }
      }
    }

    .dutch-menu__footer {
      grid-column: 1 / 4;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 2.9851vw;
      margin: auto -2.8358vw 0 -2.8358vw;
      box-shadow: inset 0 0.2239vw 0.4478vw #8a8a8a29;
      padding: 1.4925vw;
      background: #f8f8f8;
      @media (max-width: vars.$sm) {
        grid-column: 1 / 3;
        flex-direction: column;
        gap: 20px;
      }

      .dutch-menu__info {
        display: flex;
        justify-content: space-between;
        width: 37.3134vw;
        @media (max-width: vars.$md) {
          flex: 1;
        }
        @media (max-width: vars.$sm) {
          width: 100%;
        }
        .info__qty {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          width: 8.9552vw;
          flex: 0 0 8.9552vw;
          margin-right: 2.9851vw;
          border-bottom: 0.0746vw solid var(--border-color-primary);
          @media (max-width: vars.$md) {
            flex: 1;
          }
          @media (max-width: vars.$sm) {
            flex: 1;
          }
        }
        .info__amount {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          flex: 1 1 0;
          border-bottom: 0.0746vw solid var(--border-color-primary);
          @media (max-width: vars.$md) {
            flex: 2;
          }
          @media (max-width: vars.$sm) {
            flex: 2;
          }
        }
        .info__text {
          font-size: 1.4925vw;
          font-weight: 500;
          @media (max-width: vars.$md) {
            font-size: 14px;
          }
        }
        .info__number {
          font-size: 3.7313vw;
          font-weight: 700;
          @media (max-width: vars.$md) {
            font-size: 24px;
          }
        }
        .info__number--red {
          color: var(--point-color-secondary);
        }
      }
      .dutch-menu__button-group {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        width: 100%;
        width: calc(100% - 37.3134vw);
        @media (max-width: vars.$md) {
          flex: 1;
          width: 50%;
        }
        @media (max-width: vars.$sm) {
          width: 100%;
        }
        .BaseButton + .BaseButton {
          margin-left: 0.7463vw;
        }
      }
    }
  }
  .BaseButton--black {
    background: #000;
    color: #fff;
  }
  .BaseButton--burgundy {
    background: #aa093f;
    color: #fff;
  }
  .dark-theme,
  .dark-gray-theme {
    .payment-dutch {
      .BaseButton--black {
        background: #000;
        span {
          color: var(--brand-secondary);
        }
      }
    }
  }
</style>
